import { mergeClasses } from "@fluentui/react-components";
import {
  ChevronRightRegular,
  FolderRegular,
  OpenRegular,
} from "@fluentui/react-icons";
import { DocumentGlyphIcon } from "apps/project-iq/components/icons/ProjectIqIcons";
import {
  countVaultItems,
  formatVaultTimestamp,
  pluralize,
} from "apps/project-iq/pages/VaultPage/VaultPage.utils";
import { useVaultFolderRowStyles } from "./VaultFolderRow.styles";
import { IVaultFolderRowProps } from "./VaultFolderRow.types";

const VAULT_FILE_ICON_SIZE = 18;

export const VaultFolderRow = ({
  node,
  expandedFolderIds,
  onToggleFolder,
}: IVaultFolderRowProps): JSX.Element => {
  const styles = useVaultFolderRowStyles();
  const isFolder = node.kind === "folder";
  const isExpanded = isFolder && expandedFolderIds.has(node.id);
  const childListId = `vault-folder-children-${node.id}`;

  return (
    <li className={styles.item}>
      <div className={styles.row}>
        {isFolder ? (
          <button
            type="button"
            className={styles.toggleButton}
            aria-label={`${node.name} folder`}
            aria-expanded={isExpanded}
            aria-controls={isExpanded ? childListId : undefined}
            onClick={() => onToggleFolder(node.id)}
          >
            <ChevronRightRegular
              aria-hidden="true"
              className={mergeClasses(
                styles.toggleIcon,
                isExpanded && styles.toggleIconExpanded,
              )}
            />
          </button>
        ) : (
          <span className={styles.toggleSlot} aria-hidden="true" />
        )}
        <span
          className={mergeClasses(
            styles.kindIcon,
            isFolder ? styles.folderIcon : styles.fileIcon,
          )}
        >
          {isFolder ? (
            <FolderRegular aria-hidden="true" />
          ) : (
            <DocumentGlyphIcon size={VAULT_FILE_ICON_SIZE} />
          )}
        </span>
        <span
          className={mergeClasses(styles.name, isFolder && styles.folderName)}
        >
          {node.name}
          {isFolder && (
            <span className={styles.fileCount}>
              {" "}
              ({pluralize(countVaultItems(node.children).files, "file")})
            </span>
          )}
        </span>
        <span className={styles.meta}>
          {formatVaultTimestamp(node.modifiedAt)} · {node.modifiedBy}
        </span>
        {/* TODO: open the item in SharePoint. No behavior yet. */}
        <button
          type="button"
          className={styles.openButton}
          aria-label={`Open ${node.name} in SharePoint`}
        >
          <OpenRegular aria-hidden="true" />
        </button>
      </div>
      {isFolder && isExpanded && (
        <ul id={childListId} className={styles.childList}>
          {node.children.length === 0 && (
            <li className={styles.emptyMessage}>This folder is empty.</li>
          )}
          {node.children.map((child) => (
            <VaultFolderRow
              key={child.id}
              node={child}
              expandedFolderIds={expandedFolderIds}
              onToggleFolder={onToggleFolder}
            />
          ))}
        </ul>
      )}
    </li>
  );
};
